import type { QueryClient } from '@tanstack/react-query'
import type { BotState, ServerEvents } from '@apiary/shared'
import type {
  BotDetailDto,
  BotDto,
  BotEventDto,
  DashboardSummaryDto,
  McAccountDto,
  McAuthStatusDto,
  Paginated
} from './dto'
import { qk } from './query-keys'

/**
 * WS-событие → кэш TanStack Query (docs/06 §9).
 *
 * Правила, которые здесь зафиксированы:
 *  - `bot.state` патчит кэш **точечно**, без рефетча (иначе 16 ботов × тик = шторм);
 *  - `bot.deleted` инвалидирует списки — сущности больше нет, патчить нечего;
 *  - `bot.event` дописывается в буфер логов с ограничением по длине;
 *  - неизвестное событие игнорируется молча: старый клиент не должен падать
 *    от нового события сервера.
 */

/** Типы payload'ов берутся из `@apiary/shared` — локально ничего не дублируем. */
type Payload<K extends keyof ServerEvents> = Parameters<ServerEvents[K]>[0]

export type IncomingEvent =
  | { type: 'bot.state'; payload: Payload<'bot.state'> }
  | { type: 'bot.event'; payload: Payload<'bot.event'> }
  | { type: 'bot.command.ack'; payload: Payload<'bot.command.ack'> }
  | { type: 'bot.deleted'; payload: Payload<'bot.deleted'> }
  | { type: 'mcAccount.authCode'; payload: Payload<'mcAccount.authCode'> }
  | { type: 'mcAccount.status'; payload: Payload<'mcAccount.status'> }
  | { type: 'runner.status'; payload: Payload<'runner.status'> }
  | { type: 'dashboard.tick'; payload: Payload<'dashboard.tick'> }

/** Сколько строк логов держим в памяти на бота (виртуализация выдержит больше). */
export const LOG_BUFFER_LIMIT = 1000
/** Сколько событий показываем на дашборде в блоке «последние события». */
export const RECENT_EVENTS_LIMIT = 20

/**
 * Откат оптимистичного перехода: `commandId` → состояние до команды.
 * Заполняется мутациями, читается обработчиком `bot.command.ack`.
 */
const pendingCommands = new Map<string, { botId: string; previousState: BotState }>()

export function rememberCommand(commandId: string, botId: string, previousState: BotState): void {
  pendingCommands.set(commandId, { botId, previousState })
}

export function forgetCommand(commandId: string): void {
  pendingCommands.delete(commandId)
}

export function pendingCommandCount(): number {
  return pendingCommands.size
}

/** Колбэк для тостов: ack с `ok: false` должен что-то сказать пользователю. */
export type CommandFailureListener = (e: {
  botId: string
  commandId: string
  error?: string
}) => void

let commandFailureListener: CommandFailureListener | null = null

export function onCommandFailure(listener: CommandFailureListener | null): void {
  commandFailureListener = listener
}

export function applyServerEvent(queryClient: QueryClient, event: IncomingEvent): void {
  switch (event.type) {
    case 'bot.state':
      applyBotState(queryClient, event.payload)
      return
    case 'bot.event':
      applyBotEvent(queryClient, event.payload)
      return
    case 'bot.command.ack':
      applyCommandAck(queryClient, event.payload)
      return
    case 'bot.deleted':
      applyBotDeleted(queryClient, event.payload)
      return
    case 'mcAccount.authCode':
      applyAuthCode(queryClient, event.payload)
      return
    case 'mcAccount.status':
      applyAccountStatus(queryClient, event.payload)
      return
    case 'runner.status':
      applyRunnerStatus(queryClient, event.payload)
      return
    case 'dashboard.tick':
      applyDashboardTick(queryClient, event.payload)
      return
  }
}

// ─────────────────────────────── боты ───────────────────────────────

function patchBot(bot: BotDto, payload: Payload<'bot.state'>): BotDto {
  return {
    ...bot,
    state: payload.state,
    lastStateAt: payload.at,
    lastDetail: payload.detail ?? bot.lastDetail
  }
}

function applyBotState(queryClient: QueryClient, payload: Payload<'bot.state'>): void {
  // Списки: правим элемент на месте, порядок и пагинацию не трогаем.
  queryClient.setQueriesData<Paginated<BotDto>>({ queryKey: qk.bots.lists }, previous => {
    if (previous === undefined) return previous
    let changed = false
    const items = previous.items.map(bot => {
      if (bot.id !== payload.botId) return bot
      changed = true
      return patchBot(bot, payload)
    })
    return changed ? { ...previous, items } : previous
  })

  // Карточка бота.
  queryClient.setQueryData<BotDetailDto>(qk.bots.detail(payload.botId), previous => {
    if (previous === undefined) return previous
    return patchBot(previous, payload) as BotDetailDto
  })
}

function applyBotEvent(queryClient: QueryClient, payload: Payload<'bot.event'>): void {
  const entry: BotEventDto = {
    id: `${payload.at}-${payload.kind}-${Math.random().toString(36).slice(2, 8)}`,
    botId: payload.botId,
    level: payload.level,
    kind: payload.kind,
    message: payload.message,
    at: payload.at,
    ...(payload.data === undefined ? {} : { data: payload.data })
  }

  queryClient.setQueryData<Paginated<BotEventDto>>(qk.bots.events(payload.botId), previous => {
    if (previous === undefined) return { items: [entry], nextCursor: null }
    const items = [...previous.items, entry]
    return {
      ...previous,
      items: items.length > LOG_BUFFER_LIMIT ? items.slice(-LOG_BUFFER_LIMIT) : items
    }
  })

  // Блок «последние события» на дашборде: новые сверху.
  queryClient.setQueryData<BotEventDto[]>(qk.dashboard.recentEvents, previous => {
    const items = [entry, ...(previous ?? [])]
    return items.slice(0, RECENT_EVENTS_LIMIT)
  })
}

function applyCommandAck(queryClient: QueryClient, payload: Payload<'bot.command.ack'>): void {
  const pending = pendingCommands.get(payload.commandId)
  pendingCommands.delete(payload.commandId)
  if (payload.ok) return

  // docs/10 §5: откат оптимистичного перехода по `ack { ok: false }`.
  if (pending !== undefined) {
    applyBotState(queryClient, {
      botId: pending.botId,
      state: pending.previousState,
      at: new Date().toISOString()
    })
  }
  commandFailureListener?.({
    botId: payload.botId,
    commandId: payload.commandId,
    ...(payload.error === undefined ? {} : { error: payload.error })
  })
}

function applyBotDeleted(queryClient: QueryClient, payload: Payload<'bot.deleted'>): void {
  queryClient.removeQueries({ queryKey: qk.bots.detail(payload.botId) })
  queryClient.removeQueries({ queryKey: qk.bots.events(payload.botId) })
  void queryClient.invalidateQueries({ queryKey: qk.bots.root })
  void queryClient.invalidateQueries({ queryKey: qk.dashboard.summary })
}

// ───────────────────────────── аккаунты ─────────────────────────────

function applyAuthCode(queryClient: QueryClient, payload: Payload<'mcAccount.authCode'>): void {
  queryClient.setQueryData<McAuthStatusDto>(qk.mcAccounts.authStatus(payload.accountId), {
    status: 'AUTH_PENDING',
    userCode: payload.userCode,
    verificationUri: payload.verificationUri,
    expiresAt: payload.expiresAt
  })
  patchAccount(queryClient, payload.accountId, account => ({ ...account, status: 'AUTH_PENDING' }))
}

function applyAccountStatus(queryClient: QueryClient, payload: Payload<'mcAccount.status'>): void {
  queryClient.setQueryData<McAuthStatusDto>(
    qk.mcAccounts.authStatus(payload.accountId),
    previous => ({
      ...(previous ?? {}),
      status: payload.status,
      ...(payload.error === undefined ? {} : { error: payload.error })
    })
  )
  patchAccount(queryClient, payload.accountId, account => ({ ...account, status: payload.status }))
  // Ник/UUID приходят только через REST (US-2 AC3) — их дотягиваем рефетчем.
  if (payload.status === 'READY') {
    void queryClient.invalidateQueries({ queryKey: qk.mcAccounts.list })
  }
}

function patchAccount(
  queryClient: QueryClient,
  accountId: string,
  patch: (account: McAccountDto) => McAccountDto
): void {
  queryClient.setQueryData<Paginated<McAccountDto>>(qk.mcAccounts.list, previous => {
    if (previous === undefined) return previous
    let changed = false
    const items = previous.items.map(account => {
      if (account.id !== accountId) return account
      changed = true
      return patch(account)
    })
    return changed ? { ...previous, items } : previous
  })
}

// ────────────────────────── runner / дашборд ──────────────────────────

function applyRunnerStatus(queryClient: QueryClient, payload: Payload<'runner.status'>): void {
  queryClient.setQueryData<DashboardSummaryDto>(qk.dashboard.summary, previous => {
    if (previous === undefined) return previous
    let changed = false
    const runners = previous.runners.map(runner => {
      if (runner.id !== payload.runnerId) return runner
      changed = true
      return { ...runner, online: payload.online, load: payload.load ?? runner.load }
    })
    return changed ? { ...previous, runners } : previous
  })
}

function applyDashboardTick(queryClient: QueryClient, payload: Payload<'dashboard.tick'>): void {
  queryClient.setQueryData<DashboardSummaryDto>(qk.dashboard.summary, previous => {
    if (previous === undefined) return previous
    return { ...previous, botsPlaying: payload.botsPlaying, botsTotal: payload.botsTotal }
  })
}
